import * as React from 'react';
import { DialogFooter, PrimaryButton } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { cloneDeep, isEqual } from 'lodash';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { useSelector } from 'react-redux';
import { Artikel, addArtikel, updateArtikel, useArtikel } from 'api/artikel';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { BhCode } from 'formComponents/lookups/BhCode';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Label } from '@fluentui/react/lib/Label';
import { MultiSelect } from 'formComponents/Select/MultiSelect';
import { ValueType } from 'react-select';
import { SelectOption } from 'formComponents/Select/types';
import { useArtikelEigenschaftenAsOptions } from 'api/artikelEigenschaft';
import { useMaschinentypenAsOptions } from 'api/maschinentype';
import { artikelEigenschaftenByKzNrSelector, maschinentypenByNrSelector } from 'store/lookups/selector';
import { logDifference } from 'utils/helper';
import { KalkulationsblattByBlatt } from 'formComponents/lookups/KalkulationsblattByBlatt';
import { LandByLkz2 } from 'formComponents/lookups/LaenderByIkz2';
import { ArtikelAttachmentList } from './ArtikelAttachmentList';
import { AddArtikelAttachmentsData } from './types';

const styles = mergeStyleSets({
    container: {
        minHeight: 580,
    },
    firstLine: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gridColumnGap: 10,
    },
    textAndArchiv: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    image: {
        maxHeight: 120,
    },
});

interface ArtikelEditProps {
    artikelNr: string; // 'neu' = new
    setAddArtikelAttachmentsData: (data: AddArtikelAttachmentsData | null) => void;
    refetch: () => void;
}

export const ArtikelEdit: React.FC<ArtikelEditProps> = ({ artikelNr, setAddArtikelAttachmentsData, refetch }) => {
    const artikeleigenschaftenByKzNr = useSelector(artikelEigenschaftenByKzNrSelector);
    const artikelEigenschaftenAsOptions = useArtikelEigenschaftenAsOptions();

    const maschinentypenByNr = useSelector(maschinentypenByNrSelector);
    const maschinentypenAsOptions = useMaschinentypenAsOptions();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const isMounted = useIsMounted();

    const [artikel, setArtikel] = React.useState<Artikel | null>(null);

    const dbArtikel = useArtikel(
        {
            artikelNr,
            includeArchiv: true,
            withArtikelEigenschaften: true,
            withMaschinentypen: true,
            withArtikelAttachments: true,
        },
        {
            onSuccess: (data) => {
                setSavingState(SavingState.notChanged);
                setArtikel(cloneDeep(data));
            },
        },
    );

    const recalculateAndSetArtikel = React.useCallback((newArtikel: Artikel) => {
        const quadrmeter = newArtikel.breite * newArtikel.laufmeter * (newArtikel.halbeBreite ? 2 : 1);
        const nettoGew = quadrmeter * newArtikel.staerke * newArtikel.spezGew;
        const rollenGew = nettoGew + newArtikel.kernGew;

        setArtikel({
            ...newArtikel,
            quadrmeter,
            nettoGew,
            rollenGew,
        });
    }, []);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !dbArtikel.isLoading &&
            !dbArtikel.isFetching &&
            artikel !== null &&
            !isEqual(dbArtikel.data, artikel)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('artikel', artikel, dbArtikel);
        }
    }, [savingState, dbArtikel, artikel]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (artikel && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (artikelNr === 'neu') {
                addArtikel(artikel)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            dbArtikel.refetch();
                        }
                        refetch();
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateArtikel(artikel)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            dbArtikel.refetch();
                        }
                        refetch();
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const renderTextField = (column: keyof Artikel, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(artikel?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (artikel) {
                    setArtikel({
                        ...artikel,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Artikel, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={artikel?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (artikel) {
                    setArtikel({
                        ...artikel,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (column: keyof Artikel, label: string, options: FormDecimalFieldOptions = {}) => (
        <FormDecimalField
            value={(artikel?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (artikel) {
                    recalculateAndSetArtikel({
                        ...artikel,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof Artikel, label: string, className?: string) => (
        <Checkbox
            className={className || formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={artikel?.[column] as boolean}
            onChange={(event, checked) => {
                if (artikel) {
                    recalculateAndSetArtikel({
                        ...artikel,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderLand = () => (
        <LandByLkz2
            label="Ursprungsland"
            value={artikel?.land || ''}
            disabled={savingState === SavingState.saving}
            onChange={(newValue): void => {
                if (artikel) {
                    setArtikel({
                        ...artikel,
                        land: newValue,
                    });
                }
            }}
        />
    );

    const renderEinheit = () => (
        <EinheitByCode
            label="Artikel-Einheit"
            disabled={savingState === SavingState.saving}
            value={artikel?.einheit || ''}
            menuPlacement="bottom"
            onChange={(newEinheit) => {
                if (artikel) {
                    setArtikel({
                        ...artikel,
                        einheit: newEinheit,
                    });
                }
            }}
        />
    );

    const renderKalkulationsblatt = () => (
        <KalkulationsblattByBlatt
            label="Kalkulationsblatt"
            disabled={savingState === SavingState.saving}
            value={artikel?.kalkBlatt || ''}
            menuPlacement="bottom"
            allowEmpty
            onChange={(newKalkulationsblatt) => {
                if (artikel) {
                    setArtikel({
                        ...artikel,
                        kalkBlatt: newKalkulationsblatt,
                    });
                }
            }}
        />
    );

    const renderBhCode = () => (
        <BhCode
            value={artikel?.bhCode || ''}
            disabled={savingState === SavingState.saving}
            label="BH Code"
            menuPlacement="bottom"
            onChange={(newValue): void => {
                if (artikel) {
                    setArtikel({
                        ...artikel,
                        bhCode: newValue,
                    });
                }
            }}
        />
    );

    const renderArtikelEigenschaften = () => (
        <>
            <Label>Eigenschaften</Label>
            <MultiSelect
                name="artikelEigenschaftList"
                isDisabled={savingState === SavingState.saving}
                value={(artikel?.artikelEigenschaftList || [])
                    .map((artikelEigenschaft) => ({
                        value: artikelEigenschaft.eigenschaftKz + '|' + artikelEigenschaft.eigenschaftNr.toString(),
                        label: artikelEigenschaft.eigenschaftKz + ': ' + artikelEigenschaft.text,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                placeholder=""
                menuPlacement="top"
                options={artikelEigenschaftenAsOptions}
                onChange={(values: ValueType<SelectOption, true>): void => {
                    if (artikel) {
                        setArtikel({
                            ...artikel,
                            artikelEigenschaftList: ((values || []) as SelectOption[]).map((option) => {
                                if (option.value) {
                                    const [eigenschaftKz, eigenschaftNrS] = option.value.split('|');
                                    const eigenschaftNr = parseInt(eigenschaftNrS, 10);

                                    return {
                                        eigenschaftKz,
                                        eigenschaftNr,
                                        text: artikeleigenschaftenByKzNr?.[eigenschaftKz]?.[eigenschaftNr]?.text || '?',
                                    };
                                }
                                return { eigenschaftKz: '', eigenschaftNr: 0, text: 'INVALID' };
                            }),
                        });
                    }
                }}
            />
        </>
    );

    const renderArtikelMaschinentypen = () => (
        <>
            <Label>Maschinentypen</Label>
            <MultiSelect
                name="maschinentypeList"
                isDisabled={savingState === SavingState.saving}
                value={(artikel?.maschinentypeList || [])
                    .map((maschinentype) => ({
                        value: maschinentype.nr.toString(),
                        label: maschinentype.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                placeholder=""
                menuPlacement="top"
                options={maschinentypenAsOptions}
                onChange={(values: ValueType<SelectOption, true>): void => {
                    if (artikel) {
                        setArtikel({
                            ...artikel,
                            maschinentypeList: ((values || []) as SelectOption[]).map((option) => {
                                if (option.value) {
                                    const nr = parseInt(option.value, 10);

                                    return {
                                        nr,
                                        name: maschinentypenByNr?.[nr]?.name ?? nr.toString(),
                                        text: maschinentypenByNr?.[nr]?.text || '?',
                                    };
                                }
                                return { nr: 0, name: 'INVALID', text: 'INVALID' };
                            }),
                        });
                    }
                }}
            />
        </>
    );

    return (
        <>
            <div className={styles.container}>
                {routerConfirmMessage.render()}

                {(dbArtikel.isLoading || dbArtikel.isFetching) && <LoadingIndicator />}
                {artikel && (
                    <form className={formStyles.form}>
                        <div className={styles.firstLine}>
                            <div className={formStyles.gridCell}>
                                {renderTextField('nr', 'Artikel-Nr.', { disabled: artikelNr !== 'neu' })}
                            </div>
                            <div className={styles.textAndArchiv}>
                                <div className={formStyles.gridCell}>{renderTextField('text', 'Bezeichnung')}</div>
                                <div className={formStyles.gridCellRight}>
                                    {renderCheckbox('archiv', 'Archiv', formStyles.checkboxInsideFormAligned)}
                                </div>
                            </div>
                        </div>
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.gridCell}>{renderEinheit()}</div>
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('packEinheit', 'Pack-Einheit', { fixed: 2 })}
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('einhProPal', 'Einheiten je Pal./Kart.', { fixed: 2 })}
                                    </div>
                                </div>
                                <div className={formStyles.gridCell}>
                                    <div className={formStyles.twoColumnsBlock}>
                                        {renderCheckbox('bestPal', 'Palette')}
                                        {renderCheckbox('bestKart', 'Karton')}
                                    </div>
                                </div>
                                <div className={formStyles.gridCell}>{renderKalkulationsblatt()}</div>
                                <div className={formStyles.gridCell}>{renderBhCode()}</div>
                                <div className={formStyles.gridCell}>{renderTextField('zollTarif', 'Zolltarif')}</div>
                                <div className={formStyles.gridCell}>
                                    <div className={formStyles.gridCell}>{renderTextField('ean', 'EAN-Code')}</div>
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.gridCell}>{renderLand()}</div>
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('staerke', 'Stärke', { fixed: 4 })}
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        {renderTextField('kernId', 'Kern-ID (mm)', { numeric: true })}
                                    </div>
                                </div>
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('breite', 'Breite (m)', { fixed: 4 })}
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        {renderCheckbox(
                                            'halbeBreite',
                                            'Halbschlauch',
                                            formStyles.checkboxInsideFormAligned,
                                        )}
                                    </div>
                                </div>
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('laufmeter', 'Länge (m)', { fixed: 2 })}
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('quadrmeter', 'Fläche (m²)', { fixed: 2, disabled: true })}
                                    </div>
                                </div>
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('spezGew', 'Spez. Gew. (kg/dm³)', { fixed: 4 })}
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('kernGew', 'Kerngewicht (kg)', { fixed: 4 })}
                                    </div>
                                </div>
                                <div className={formStyles.twoColumnsBlock}>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('nettoGew', 'Nettogewicht (kg)', {
                                            fixed: 2,
                                            disabled: true,
                                        })}
                                    </div>
                                    <div className={formStyles.gridCell}>
                                        {renderDecimalField('rollenGew', 'Bruttogewicht (kg)', {
                                            fixed: 2,
                                            disabled: true,
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.gridCell}>
                                    <div className={formStyles.gridCell}>
                                        {renderTextField('prodInfoUes', 'Produkt-Info (Überschrift)')}
                                    </div>
                                </div>
                                <div className={formStyles.gridCell}>
                                    <div className={formStyles.gridCell}>
                                        {renderTextarea('prodInfo', 'Produkt-Info')}
                                    </div>
                                </div>
                                <div className={formStyles.gridCell}>
                                    <div className={formStyles.gridCell}>
                                        {renderTextarea('bemerkung', 'Bemerkung')}
                                    </div>
                                </div>
                                <div className={formStyles.gridCell}>
                                    <div className={formStyles.gridCell}>
                                        {dbArtikel.data?.artikelAttachmentList && artikelNr !== 'neu' && (
                                            <ArtikelAttachmentList
                                                artikelNr={dbArtikel.data.nr}
                                                artikelAttachmentList={dbArtikel.data.artikelAttachmentList}
                                                refetchArtikel={() => dbArtikel.refetch()}
                                                onAdd={() =>
                                                    setAddArtikelAttachmentsData({
                                                        artikelNr,
                                                        refetch: () => dbArtikel.refetch(),
                                                    })
                                                }
                                            />
                                        )}
                                    </div>

                                    {/* artikel.bild ? (
                                    <img
                                        className={styles.image}
                                        alt=""
                                        src={
                                            getApiUrl('attachment/artikelimage') +
                                            '/?fileName=' +
                                            encodeURIComponent(artikel.bild)
                                        }
                                    />
                                ) : (
                                    '(Kein Bild)'
                                ) */}
                                </div>
                                {/*
                                <div className={formStyles.gridCell}>
                                    {renderTextField('doku', 'Beschreibung (Datei)', { disabled: true })}
                                </div>
                                */}
                            </div>
                        </div>
                        <div className={formStyles.line}>{renderArtikelEigenschaften()}</div>
                        <div className={formStyles.line}>{renderArtikelMaschinentypen()}</div>

                        <Timestamps
                            createdDate={artikel?.anlageDat}
                            createdDecimalTime={artikel?.anlageUhr}
                            createdUser={artikel?.anlageUser}
                            modifiedDate={artikel.updDat}
                            modifiedDecimalTime={artikel.updUhr}
                            modifiedUser={artikel.updUser}
                        />

                        {savingState === SavingState.error && (
                            <div className={formStyles.error}>Fehler beim Speichern.</div>
                        )}
                        <DialogFooter>
                            {savingState === SavingState.saving ? (
                                <PrimaryButton onClick={save} text="Speichere..." disabled />
                            ) : (
                                <PrimaryButton
                                    onClick={save}
                                    text="Speichern"
                                    disabled={savingState === SavingState.notChanged || dbArtikel.isLoading}
                                />
                            )}
                        </DialogFooter>
                    </form>
                )}
            </div>
        </>
    );
};
